import { normalizedLandingPath } from "./landing-attribution-policy";
import { isSensitivePaymentPath } from "./sensitive-route-policy";

type CapabilityRoute = Readonly<{
  prefix: string;
  template: string;
}>;

/**
 * Browser paths whose first dynamic segment is a bearer capability over paid
 * or personal data. Keep this list ordered from most-specific to least-specific.
 * The templates are measurement dimensions, never navigable URLs.
 */
export const CAPABILITY_TELEMETRY_ROUTES: readonly CapabilityRoute[] = [
  { prefix: "/kundali/update", template: "/kundali/update/[readingId]" },
  { prefix: "/my-panchang", template: "/my-panchang/[readingId]" },
  { prefix: "/kundali", template: "/kundali/[readingId]" },
  { prefix: "/chat", template: "/chat/[readingId]" },
  { prefix: "/match", template: "/match/[token]" },
  { prefix: "/r/offer", template: "/r/offer/[token]" },
  { prefix: "/r", template: "/r/[token]" },
] as const;

function capabilityRouteFor(pathname: string): CapabilityRoute | undefined {
  const normalized = normalizedLandingPath(pathname);
  return CAPABILITY_TELEMETRY_ROUTES.find(({ prefix }) => normalized.startsWith(`${prefix}/`));
}

function pathFromValue(value: string | null | undefined): string {
  if (!value || !/^https?:\/\//i.test(value)) return value || "/";
  try {
    return new URL(value).pathname;
  } catch {
    return value;
  }
}

function pathWithoutQueryOrHash(value: string | null | undefined): string {
  const raw = pathFromValue(value).split("?")[0]?.split("#")[0] || "/";
  const withLeadingSlash = raw.startsWith("/") ? raw : `/${raw}`;
  return withLeadingSlash.replace(/\/{2,}/g, "/");
}

export function isCapabilityTelemetryPath(pathname: string | null | undefined): boolean {
  return Boolean(pathname && capabilityRouteFor(pathFromValue(pathname)));
}

export function privacySafeTelemetryPath(pathname: string | null | undefined): string {
  const path = pathWithoutQueryOrHash(pathname);
  return capabilityRouteFor(path)?.template ?? path;
}

/** Strip query/hash and replace any private route identifier with its template. */
export function privacySafeTelemetryUrl(value: string | null | undefined): string {
  if (!value) return "";
  try {
    const parsed = new URL(value);
    return `${parsed.origin}${privacySafeTelemetryPath(parsed.pathname)}`;
  } catch {
    return privacySafeTelemetryPath(value);
  }
}

const CAPABILITY_TEXT_PATTERNS = [
  /\/kundali\/update\/(?![\[{])[^/?#\s'"<>,)\]}{]+/gi,
  /\/my-panchang\/(?![\[{])[^/?#\s'"<>,)\]}{]+/gi,
  /\/kundali\/(?!update(?:\/|$)|[\[{])[^/?#\s'"<>,)\]}{]+/gi,
  /\/chat\/(?![\[{])[^/?#\s'"<>,)\]}{]+/gi,
  /\/match\/(?![\[{])[^/?#\s'"<>,)\]}{]+/gi,
  /\/r\/offer\/(?![\[{])[^/?#\s'"<>,)\]}{]+/gi,
  /\/r\/(?!offer(?:\/|$)|[\[{])[^/?#\s'"<>,)\]}{]+/gi,
] as const;

export function redactCapabilityRoutesInText(value: string): string {
  return CAPABILITY_TEXT_PATTERNS.reduce(
    (redacted, pattern, index) =>
      redacted.replace(pattern, CAPABILITY_TELEMETRY_ROUTES[index]?.template ?? "[private]"),
    value
  );
}

const PATH_PROPERTY_KEYS = new Set(["landing_path", "page_path", "route", "source_path"]);
const URL_PROPERTY_KEYS = new Set(["page_location", "page_referrer", "page_url"]);

function privacySafeTelemetryValue(key: string, value: unknown): unknown {
  if (typeof value === "string") {
    if (PATH_PROPERTY_KEYS.has(key)) return privacySafeTelemetryPath(value);
    if (URL_PROPERTY_KEYS.has(key)) return privacySafeTelemetryUrl(value);
    return redactCapabilityRoutesInText(value);
  }
  if (Array.isArray(value)) {
    return value.map((item) => privacySafeTelemetryValue(key, item));
  }
  if (value && Object.getPrototypeOf(value) === Object.prototype) {
    return privacySafeTelemetryProperties(value as Record<string, unknown>);
  }
  return value;
}

export function privacySafeTelemetryProperties(
  properties: Readonly<Record<string, unknown>>
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(properties).map(([key, value]) => [key, privacySafeTelemetryValue(key, value)])
  );
}

export function thirdPartyTelemetryAllowedForDocument(pathname: string): boolean {
  return !isSensitivePaymentPath(pathname) && !isCapabilityTelemetryPath(pathname);
}

export function isBrowserThirdPartyTelemetrySuppressed(): boolean {
  return (
    typeof window !== "undefined" &&
    !thirdPartyTelemetryAllowedForDocument(window.location.pathname)
  );
}
